import { getPublicFileOrigin } from "../config";

/**
 * Normalize API `images`: may be an array, a JSON string, or missing.
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeProductImageList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((x) => (x == null ? "" : String(x).trim()))
      .filter(Boolean);
  }
  if (typeof raw === "string") {
    const s = raw.trim();
    if (!s) return [];
    if (s.startsWith("[")) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) {
          return parsed
            .map((x) => (x == null ? "" : String(x).trim()))
            .filter(Boolean);
        }
      } catch {
        /* fall through */
      }
    }
    return [s];
  }
  return [];
}

/**
 * Absolute URL for one stored image path or external URL.
 * @param {unknown} raw
 * @returns {string}
 */
export function resolveProductImageSrc(raw) {
  if (raw == null) return "/images/no-image.svg";
  const url = String(raw).trim();
  if (!url) return "/images/no-image.svg";
  if (url.startsWith("data:")) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/images/")) return url;

  const PUBLIC_FILE_ORIGIN = getPublicFileOrigin();
  if (url.startsWith("/")) return `${PUBLIC_FILE_ORIGIN}${url}`;
  return `${PUBLIC_FILE_ORIGIN}/${url}`;
}

/**
 * Resolve first usable image URL for display (absolute URLs, /public paths, backend-relative paths).
 * @param {Record<string, unknown>} product
 * @returns {string}
 */
export function getPrimaryProductImageSrc(product) {
  const list = normalizeProductImageList(product?.images);
  const fromUrls = normalizeProductImageList(product?.urls);
  const legacy =
    product?.image_url != null && String(product.image_url).trim()
      ? [String(product.image_url).trim()]
      : [];
  const raw = list[0] || fromUrls[0] || legacy[0] || "";
  return resolveProductImageSrc(raw);
}

/**
 * Full gallery URLs for product detail (never empty — falls back to placeholder).
 * @param {Record<string, unknown>} product
 * @returns {string[]}
 */
export function getProductImageGallerySrcs(product) {
  const list = [
    ...normalizeProductImageList(product?.images),
    ...normalizeProductImageList(product?.urls),
  ];
  if (product?.image_url != null && String(product.image_url).trim()) {
    list.push(String(product.image_url).trim());
  }
  const urls = [...new Set(list)].map(resolveProductImageSrc).filter(Boolean);
  return urls.length ? urls : ["/images/no-image.svg"];
}
